import { useState, useEffect, useRef } from "react"
import ExpirationPaymentButtons from "../ExpirationPaymentButtons/ExpirationPaymentButtons"
import useStrings from "../../../localization/useStrings"
import useQueryParams from "../../../hooks/useQueryParams"
import useHasPro from "../../../hooks/useHasPro"
import useSwitchToBasicShown from "../../../hooks/useSwitchToBasicShown"
import { useAppState } from "../../../context/appStateContext"
import { usePurchase } from "../../../context/purchaseContext"
import { useExpiration } from "../../../context/expirationContext"
import { useExpirationStrings } from "../useExpirationStrings"
import native from "../../../native/native"
import { Background } from "../BubblePaywallSVG/BubblePaywallSVG"
import BubbleFeatureList from "../../../components/BubbleFeatureList/BubbleFeatureList"
import { PlanToggleButton } from "../PlanToggleButton/PlanToggleButton"
import { CompareButton } from "../CompareButton/CompareButton"
import CloseButton from "../../../components/CloseButton/CloseButton"
import styles from "./ExpirationStep2.module.scss"
import featureStyles from "../../../components/BubbleFeatureList/BubbleFeatureList.module.scss"

interface Props {
  onLifetimeClick: () => void
}

const ExpirationStep2 = ({ onLifetimeClick }: Props) => {
  const [additionalPadding, setAdditionalPadding] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const switchToBasicRef = useRef<HTMLButtonElement>(null)
  const strings = useStrings()
  const queryParams = useQueryParams()
  const { state: appState } = useAppState()
  const { state: purchaseState } = usePurchase()
  const expirationStrings = useExpirationStrings()

  const showUltimateToggle = queryParams.showUltimateInExpiration
  const isUltimate = purchaseState.ultimateSelected
  const hasPro = useHasPro()
  const [showCloseButton, setShowCloseButton] = useState(false)

  const { state: expirationState } = useExpiration()

  useEffect(() => {
    if (hasPro && !expirationState.showWarmup) {
      const timer = setTimeout(() => setShowCloseButton(true), 3000)
      return () => clearTimeout(timer)
    }
  }, [hasPro, expirationState.showWarmup])

  useEffect(() => {
    if (queryParams.expandPaymentPadding) {
      const calculatePadding = () => {
        if (!containerRef.current) return

        // Wait for styles to be applied
        requestAnimationFrame(() => {
          const featuresSection = containerRef.current?.querySelector(
            `.${featureStyles.featuresSection}`,
          )
          if (!featuresSection) return

          // Calculate height up to the end of features section
          const featuresRect = featuresSection.getBoundingClientRect()
          const containerRect = containerRef.current!.getBoundingClientRect()
          const contentHeightToFeatures =
            featuresRect.bottom - containerRect.top

          const viewportHeight = window.innerHeight
          const topInset = appState.initialData?.topInset || 0
          const bottomInset = appState.initialData?.originalBottomInset || 0

          console.log("Debug padding calculation:", {
            contentHeightToFeatures,
            viewportHeight,
            topInset,
            bottomInset,
            availableHeight: viewportHeight - topInset - bottomInset,
          })

          // Calculate available height (viewport minus safe areas)
          const availableHeight = viewportHeight - topInset - bottomInset

          // If content up to features is shorter than available height, calculate padding needed
          if (contentHeightToFeatures < availableHeight) {
            const paddingNeeded =
              (availableHeight - contentHeightToFeatures) / 2
            const finalPadding = Math.max(0, paddingNeeded)
            console.log("Setting additional padding:", finalPadding)
            setAdditionalPadding(finalPadding)
          } else {
            console.log("Content already fills viewport, no padding needed")
            setAdditionalPadding(0)
          }
        })
      }

      // Use setTimeout to ensure DOM is ready
      const timeout = setTimeout(calculatePadding, 40)
      window.addEventListener("resize", calculatePadding)
      return () => {
        clearTimeout(timeout)
        window.removeEventListener("resize", calculatePadding)
      }
    } else {
      setAdditionalPadding(0)
    }
  }, [queryParams.expandPaymentPadding, appState.initialData])

  useSwitchToBasicShown(
    scrollContainerRef,
    switchToBasicRef,
    undefined,
    !expirationState.showWarmup,
  )

  const restoreSectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!queryParams.peekScrollable) return

    const section = restoreSectionRef.current
    const root = scrollContainerRef.current
    if (!section || !root) return

    const adjust = () => {
      const btn = section.querySelector("button")
      if (!btn) return

      const btnRect = btn.getBoundingClientRect()
      const overflow = btnRect.bottom - window.innerHeight

      if (overflow > 0) {
        const offset = -(overflow - 7)
        section.style.transform = `translateY(${offset}px)`
      }
    }

    section.style.transform = ""
    const timeout = setTimeout(adjust, 50)
    return () => clearTimeout(timeout)
  }, [additionalPadding, queryParams.peekScrollable])

  const handleSwitchToBasic = () => {
    native.postNativeEvent("close")
  }

  const handleRestorePurchases = () => {
    native.postNativeEvent("already_purchased")
  }

  const handleTermsClick = () => {
    native.postNativeEvent("terms")
  }

  const handlePrivacyClick = () => {
    native.postNativeEvent("privacy")
  }

  return (
    <div
      className={styles.container}
      ref={containerRef}
      style={
        {
          "--inset-offset": `${(appState.initialData?.topInset ?? 0) + (appState.initialData?.originalBottomInset ?? 0)}px`,
        } as React.CSSProperties
      }
    >
      {showCloseButton && (
        <CloseButton
          className={styles.closeButton}
          onClick={() => native.postNativeEvent("close")}
          style={{ top: `${(appState.initialData?.topInset ?? 0) + 12}px` }}
        />
      )}
      <div className={styles.scrollContainer} ref={scrollContainerRef}>
        <div
          className={styles.mainContent}
          style={
            queryParams.expandPaymentPadding ? { minHeight: "auto" } : undefined
          }
        >
          {/* Compare Button */}
          {showUltimateToggle && (
            <div className={styles.compareButtonWrapper}>
              <CompareButton />
            </div>
          )}

          {/* Header */}
          <div className={styles.header}>
            <label className={styles.title}>
              {expirationStrings.step2.title}
            </label>
            <label className={styles.subtitle}>
              {expirationStrings.step2.subtitle}
            </label>
          </div>

          {/* Background Wave */}
          <div className={styles.headerWaveBackground}>
            <Background
              primaryColor="var(--wave-primary-color)"
              useGradient={isUltimate}
            />
          </div>

          {/* Payment Buttons */}
          <div
            className={`${styles.paymentSection} ${showUltimateToggle ? styles.withToggle : ""}`}
            style={
              queryParams.expandPaymentPadding && additionalPadding > 0
                ? {
                    paddingTop: showUltimateToggle
                      ? `${6 + additionalPadding}px`
                      : `${22 + additionalPadding}px`,
                    paddingBottom: showUltimateToggle
                      ? `${Math.max(0, 6 + additionalPadding)}px`
                      : `${22 + additionalPadding}px`,
                    background: isUltimate
                      ? "linear-gradient(90deg, var(--ultimate-gradient-start) 0%, var(--ultimate-gradient-end) 100%)"
                      : undefined,
                  }
                : isUltimate
                  ? {
                      background:
                        "linear-gradient(90deg, var(--ultimate-gradient-start) 0%, var(--ultimate-gradient-end) 100%)",
                    }
                  : undefined
            }
          >
            {showUltimateToggle && (
              <div className={styles.toggleButtonContainer}>
                <PlanToggleButton />
              </div>
            )}
            <ExpirationPaymentButtons onLifetimeClick={onLifetimeClick} />
          </div>

          {/* Bottom Background Wave */}
          <div className={styles.footerWaveBackground}>
            <Background
              primaryColor="var(--wave-primary-color)"
              useGradient={isUltimate}
            />
          </div>

          {/* Features List */}
          <BubbleFeatureList
            useUltimateColor={isUltimate}
            iconColor="var(--step2-color-primary)"
          />
        </div>

        <div ref={restoreSectionRef}>
          {/* Restore Purchases */}
          <div className={styles.restoreSection}>
            <button
              className={`${styles.restoreButton} ${isUltimate ? styles.ultimate : ""}`}
              onClick={handleRestorePurchases}
            >
              {strings.bubbleExpiration.restorePurchases}
            </button>
          </div>

          {/* Basic Plan Section */}
          {!hasPro && (
            <div className={styles.basicSection}>
              <div className={styles.basicCard}>
                <div className={styles.basicHeader}>
                  <label className={styles.basicTitle}>
                    {expirationStrings.step2.basicSectionTitle}
                  </label>
                  <span className={styles.freeBadge}>
                    {expirationStrings.step2.basicSectionBadge}
                  </span>
                </div>
                <label className={styles.basicDescription}>
                  {expirationStrings.step2.basicSectionDescription}
                </label>
                <button
                  ref={switchToBasicRef}
                  className={`${styles.switchToBasicButton} ${isUltimate ? styles.ultimate : ""}`}
                  onClick={handleSwitchToBasic}
                >
                  {expirationStrings.step2.basicSectionCta}
                </button>
              </div>
            </div>
          )}

          {/* Footer Links */}
          <div className={styles.footerLinks}>
            <button
              className={`${styles.link} ${isUltimate ? styles.ultimate : ""}`}
              onClick={handleTermsClick}
            >
              {strings.bubbleExpiration.termsOfService}
            </button>
            <span className={styles.separator}>|</span>
            <button
              className={`${styles.link} ${isUltimate ? styles.ultimate : ""}`}
              onClick={handlePrivacyClick}
            >
              {strings.bubbleExpiration.privacy}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExpirationStep2
